import { loadStripeTerminal } from "@stripe/terminal-js";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authHeader, clearToken, isUnauthorized } from "../utils/staffAuth";
import {
  ENVELOPE_NAME_MAX,
  canCancelGiftSale,
  centsToDollars,
  counterSaleBody,
  counterSaleCents,
  counterSaleRefusal,
} from "../utils/giftCertificate";

const pathURL = process.env.REACT_APP_API_URL;
// The same reader the garment checkout and the Products screen use.
const TERMINAL_LOCATION_ID =
  process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID || "tml_Gp0bwwHQWlsS5n";

const SIGNED_OUT =
  "This device has been signed out. Refresh the page, sign in again, and take the payment. Nothing was charged.";

const AMOUNTS = [2500, 5000, 10000, 20000];

/**
 * Sell a gift card at the counter, on its own, with or without one of
 * Natalie's stitched envelopes (Calley, 2026-10-27: one flow for staff, so a
 * customer buying only a gift card does not send anyone into superadmin).
 *
 * Like the Products screen, this is its own small sale with its own tap. It
 * never touches the garment basket. A card bought WITH a repair is a box in
 * the checkout instead (components/Checkout.jsx), because that is one payment
 * for both.
 *
 * The server prices the sale and the reader's charge from the certificate it
 * holds (fxrybackend controllers/giftCertificates.js): the amount below is
 * only what staff see. Sales tax on the envelope is Stripe's figure, added
 * when the payment starts, so the reader asks for a little more than the
 * total shown here. A gift card itself is never taxed.
 */
function GiftCard() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(10000);
  const [other, setOther] = useState("");
  const [code, setCode] = useState("");
  const [envelopeStyle, setEnvelopeStyle] = useState("none");
  const [envelopeName, setEnvelopeName] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [options, setOptions] = useState(null);
  // idle | connecting | starting | collecting | cancelling | processing |
  // recording | paid | error
  const [stage, setStage] = useState("idle");
  const [message, setMessage] = useState("");
  const [paid, setPaid] = useState(null);
  const terminalRef = useRef(null);
  // The stage as it is right now. `stage` inside a click handler is the value
  // that render captured, and Cancel has to know whether the card has reached
  // processPayment yet: the one difference between "nothing was charged" and
  // "that is a refund now".
  const phaseRef = useRef("idle");
  // Set when staff press Cancel, so the sale still running below stops
  // quietly instead of reporting a decline nobody caused.
  const stoppedRef = useRef(false);
  // The payment to call off, once Stripe has one.
  const intentIdRef = useRef(null);

  // Prices and how many envelopes are left, from the same place the website
  // reads them, so one price change moves both.
  useEffect(() => {
    axios
      .get(`${pathURL}gift/options`)
      .then((response) => setOptions(response.data || null))
      .catch(() => setOptions(null));
  }, []);

  const busy = !["idle", "error", "paid"].includes(stage);
  const amountCents =
    amount === "other"
      ? Math.round((Number(String(other).replace(/[$,\s]/g, "")) || 0) * 100)
      : amount;
  const prices = options?.envelope_prices || { ready: 6500, named: 9500 };
  // How many of the fifteen ready-made ones are left. A named one is not one
  // of them, so this never limits that choice.
  const readyLeft = Number.isFinite(options?.envelopes_left)
    ? options.envelopes_left
    : null;
  // An older backend sends no figure, so offer it and let the server refuse.
  const readyAvailable = readyLeft === null || readyLeft > 0;
  const envelopeCents =
    envelopeStyle === "none" ? 0 : Number(prices[envelopeStyle]) || 0;
  const total = counterSaleCents({ amountCents, envelopeCents });
  const blocker = counterSaleRefusal({
    amountCents,
    code,
    envelopeStyle,
    envelopeName,
    envelopesLeft: readyLeft,
  });

  const fail = (text) => {
    phaseRef.current = "error";
    setStage("error");
    setMessage(text);
  };

  /** Connect once and keep it: discovery takes seconds. */
  const connectReader = async (signedOut) => {
    let terminal = terminalRef.current;
    if (!terminal) {
      const StripeTerminal = await loadStripeTerminal();
      terminal = StripeTerminal.create({
        onFetchConnectionToken: async () => {
          try {
            const resp = await axios.post(
              `${pathURL}order/terminal/connection-token`,
              null,
              { headers: authHeader() },
            );
            return resp.data.secret;
          } catch (tokenError) {
            // Stripe's SDK swallows this 401 and reports its own failure,
            // so remember it and tell staff to sign in again.
            if (isUnauthorized(tokenError)) signedOut.yes = true;
            throw tokenError;
          }
        },
        onUnexpectedReaderDisconnect: () => {
          terminalRef.current = null;
        },
      });
      terminalRef.current = terminal;
    }
    if (terminal.getConnectionStatus() === "connected") return terminal;
    const discovered = await terminal.discoverReaders({
      location: TERMINAL_LOCATION_ID,
    });
    if (signedOut.yes) throw new Error("signed-out");
    if (discovered.error || !discovered.discoveredReaders?.length) {
      throw new Error(
        "Couldn't find the reader. Make sure it's powered on and connected to Wi-Fi. Nothing was charged.",
      );
    }
    const connected = await terminal.connectReader(
      discovered.discoveredReaders[0],
    );
    if (signedOut.yes) throw new Error("signed-out");
    if (connected.error) {
      throw new Error(
        `${connected.error.message || "Couldn't connect to the reader."} Nothing was charged.`,
      );
    }
    return terminal;
  };

  /**
   * Staff press Cancel: the customer changed their mind at the reader.
   *
   * Stopping the reader comes first. The reader is driven from this screen,
   * so while it is still asking for a card Stripe will not let the payment be
   * cancelled underneath it, and without this there was no way out of the tap
   * at all: the reader kept asking and the only escape was to restart it
   * (Calley, 4 Nov 2026, after it happened on a product sale).
   *
   * The server call is the one the garment checkout and extra-work charges
   * already use. It refuses a payment that went through, and it releases the
   * hold this sale put on the card's balance.
   */
  const cancelPayment = async () => {
    if (stoppedRef.current) return;
    if (!canCancelGiftSale(phaseRef.current)) {
      setMessage(
        "The card is being read now. Wait for it to finish, then refund it if they don't want it.",
      );
      return;
    }
    stoppedRef.current = true;
    phaseRef.current = "cancelling";
    setStage("cancelling");
    setMessage("Stopping the payment. Nothing has been charged.");

    try {
      await terminalRef.current?.cancelCollectPaymentMethod();
    } catch (readerError) {
      // The reader had already finished asking, or was never asking. The
      // payment below is what decides whether money can still move.
      console.error("[GIFT] stopping the reader:", readerError);
    }

    const paymentIntentId = intentIdRef.current;
    if (paymentIntentId) {
      try {
        await axios.post(
          `${pathURL}order/terminal/cancel-payment-intent`,
          { paymentIntentId },
          { headers: authHeader() },
        );
      } catch (cancelError) {
        intentIdRef.current = null;
        return fail(
          cancelError?.response?.data?.message ||
            `Couldn't cancel payment ${paymentIntentId}. Check it in Stripe before charging anything again.`,
        );
      }
    }

    // The card itself is untouched: a drawer card is only made live once the
    // payment settles, so nothing here needs undoing.
    intentIdRef.current = null;
    phaseRef.current = "idle";
    setStage("idle");
    setMessage("");
    return undefined;
  };

  const takePayment = async () => {
    if (blocker || busy) return;
    const signedOut = { yes: false };
    let sale = null;
    stoppedRef.current = false;
    intentIdRef.current = null;
    // Tracked on a ref rather than read off `stage`: setStage does not update
    // the value this function captured, so the catch below would misjudge
    // whether money had moved, and Cancel reads the same ref.
    const enter = (next) => {
      phaseRef.current = next;
      setStage(next);
    };
    try {
      enter("connecting");
      setMessage("Connecting to the reader...");
      const terminal = await connectReader(signedOut);

      // The sale is saved before any card is asked for. A drawer card stays
      // unsold until it is paid for, so an abandoned tap leaves it sellable.
      enter("starting");
      setMessage("Starting the sale...");
      const started = await axios.post(
        `${pathURL}gift/sell`,
        counterSaleBody({
          amountCents,
          code,
          envelopeStyle,
          envelopeName,
          buyerName,
          buyerEmail,
        }),
        { headers: authHeader() },
      );
      sale = started.data;
      const intent = await axios.post(
        `${pathURL}gift/${sale.id}/terminal-intent`,
        null,
        { headers: authHeader() },
      );
      intentIdRef.current = intent.data.paymentIntentId;

      enter("collecting");
      setMessage(
        `Tap, insert, or swipe the card on the reader now. ${centsToDollars(
          intent.data.amount_cents,
        )}`,
      );
      const collected = await terminal.collectPaymentMethod(
        intent.data.clientSecret,
      );
      // Staff pressed Cancel: stopping the reader is what ended the wait
      // above, and cancelPayment has already called the payment off and said
      // so on screen. Saying anything here would talk over it.
      if (stoppedRef.current) return undefined;
      if (collected.error) {
        return fail(
          `${collected.error.message || "The card was declined or the tap was cancelled."} Nothing was charged.`,
        );
      }

      enter("processing");
      setMessage("Processing payment...");
      const processed = await terminal.processPayment(collected.paymentIntent);
      if (processed.error || processed.paymentIntent?.status !== "succeeded") {
        return fail(
          `${processed.error?.message || "The payment did not go through."} Nothing was charged.`,
        );
      }

      // Only the server makes the card live, from Stripe's own answer, so a
      // tap that lands while this screen closes is not lost.
      enter("recording");
      setMessage("Payment approved. Making the card live...");
      await axios.post(
        `${pathURL}gift/${sale.id}/terminal-settle`,
        { paymentIntentId: intent.data.paymentIntentId },
        { headers: authHeader() },
      );
      setPaid({
        ...sale,
        charged_cents: intent.data.amount_cents,
        tax_cents: intent.data.tax_cents,
        envelope_cents: intent.data.envelope_cents,
        envelope_style: envelopeStyle,
        envelope_name: envelopeName.trim(),
      });
      intentIdRef.current = null;
      phaseRef.current = "paid";
      setStage("paid");
      setMessage("");
    } catch (error) {
      console.error("[GIFT] counter sale failed:", error);
      // Only recording runs after the payment went through, so only there has
      // the money definitely moved. Anywhere earlier nothing was taken.
      // Staff pressed Cancel and the reader threw on the way out rather than
      // answering. cancelPayment owns the screen from there.
      if (stoppedRef.current && phaseRef.current !== "recording") {
        return undefined;
      }
      if (phaseRef.current === "recording") {
        return fail(
          `THE CARD WAS CHARGED. Do not charge it again. Open the Gift cards page in superadmin and check the card before anything else.`,
        );
      }
      if (signedOut.yes || isUnauthorized(error)) {
        clearToken();
        return fail(SIGNED_OUT);
      }
      return fail(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong talking to the reader. Nothing was charged.",
      );
    }
    return undefined;
  };

  const newSale = () => {
    setAmount(10000);
    setOther("");
    setCode("");
    setEnvelopeStyle("none");
    setEnvelopeName("");
    setBuyerName("");
    setBuyerEmail("");
    setPaid(null);
    stoppedRef.current = false;
    intentIdRef.current = null;
    phaseRef.current = "idle";
    setStage("idle");
    setMessage("");
  };

  const payLabel = {
    idle: `Take ${total ? centsToDollars(total) : "payment"} on the reader`,
    connecting: "Finding the reader...",
    starting: "Starting...",
    collecting: "Tap or insert the card",
    cancelling: "Stopping...",
    processing: "Processing...",
    recording: "Making the card live...",
    error: "Try again",
    paid: "Paid",
  }[stage];

  return (
    <section className="items-section same-bg-section relative">
      <div className="container padding-x-all">
        <div className="products-wrap">
          <div className="products-screen">
            <button
              type="button"
              className="products-back"
              onClick={() => navigate("/")}
              disabled={busy}
            >
              ← Garments
            </button>
            <h2>Gift card</h2>

            {stage === "paid" && paid ? (
              <div className="wears-box products-paid" role="status">
                <h5>Paid</h5>
                <p className="products-paid-total">
                  {centsToDollars(paid.charged_cents)}
                </p>
                <ul className="products-lines">
                  <li>
                    <span>Gift card {paid.display_code}</span>
                    <span>{centsToDollars(paid.amount_cents)}</span>
                  </li>
                  {paid.envelope_cents > 0 && (
                    <li>
                      <span>
                        Stitched envelope
                        {paid.envelope_style === "named"
                          ? ` for ${paid.envelope_name}`
                          : ""}
                      </span>
                      <span>{centsToDollars(paid.envelope_cents)}</span>
                    </li>
                  )}
                  {paid.tax_cents > 0 && (
                    <li>
                      <span>Sales tax</span>
                      <span>{centsToDollars(paid.tax_cents)}</span>
                    </li>
                  )}
                </ul>
                <p className="para">
                  Hand the card over.
                  {paid.envelope_style === "named"
                    ? " The envelope is on the Envelopes to do list in superadmin: Natalie stitches the name, then they collect it."
                    : paid.envelope_cents > 0
                      ? " Put the card in the envelope and hand it over."
                      : ""}
                  {paid.emailed ? " The code is emailed too." : ""}
                </p>
                <div className="products-paid-actions">
                  <button
                    type="button"
                    className="section-main-btn"
                    onClick={newSale}
                  >
                    New sale
                  </button>
                  <button
                    type="button"
                    className="custom-item-btn"
                    onClick={() => navigate("/")}
                  >
                    Back to garments
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="para">
                  A gift card on its own. Buying one with a repair? Add it in
                  the checkout instead, so it is one payment.
                </p>

                <div className="wears-box">
                  <p className="para">How much?</p>
                  <ul className="items-wears-listing">
                    {AMOUNTS.map((value) => (
                      <li key={value}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setAmount(value)}
                          className={amount === value ? "item-selected" : ""}
                        >
                          {centsToDollars(value).replace(".00", "")}
                        </button>
                      </li>
                    ))}
                    <li>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setAmount("other")}
                        className={amount === "other" ? "item-selected" : ""}
                      >
                        Other
                      </button>
                    </li>
                  </ul>
                  {amount === "other" && (
                    <div className="field-box">
                      <label className="label-on-line">
                        Amount, $25 or more
                      </label>
                      <input
                        inputMode="decimal"
                        value={other}
                        disabled={busy}
                        onChange={(e) => setOther(e.target.value)}
                        placeholder="25"
                      />
                    </div>
                  )}

                  <div className="field-box">
                    <label className="label-on-line">
                      Code from the back of a printed card
                    </label>
                    <input
                      value={code}
                      disabled={busy}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="XXXX-XXXX-XXXX"
                    />
                  </div>
                  <p className="para">
                    No printed card to hand? Sell it on the Gift cards page in
                    superadmin, which can print a sticker for a blank card.
                  </p>

                  {/* The ready-made ones are stock and run out; a named one
                      is cut and stitched to order, so it is always on offer
                      (Calley, 27 Oct 2026). */}
                  <p className="para">
                    A stitched envelope?{" "}
                    {readyLeft === null
                      ? ""
                      : readyLeft > 0
                        ? `(${readyLeft} ready-made left)`
                        : "(the ready-made ones have all gone)"}
                  </p>
                  <ul className="items-wears-listing">
                    {[
                      ["none", "No envelope", 0],
                      ...(readyAvailable
                        ? [["ready", "Ready-made", prices.ready]]
                        : []),
                      ["named", "With a name", prices.named],
                    ].map(([value, label, price]) => (
                      <li key={value}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setEnvelopeStyle(value)}
                          className={
                            envelopeStyle === value ? "item-selected" : ""
                          }
                        >
                          {label}
                          {price
                            ? ` ${centsToDollars(price).replace(".00", "")}`
                            : ""}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {envelopeStyle === "named" && (
                    <div className="field-box">
                      <label className="label-on-line">Name to stitch</label>
                      <input
                        value={envelopeName}
                        disabled={busy}
                        maxLength={ENVELOPE_NAME_MAX}
                        onChange={(e) => setEnvelopeName(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="field-box">
                    <label className="label-on-line">
                      Their name (optional)
                    </label>
                    <input
                      value={buyerName}
                      disabled={busy}
                      onChange={(e) => setBuyerName(e.target.value)}
                    />
                  </div>
                  <div className="field-box">
                    <label className="label-on-line">
                      Their email (optional, emails the code too)
                    </label>
                    <input
                      type="email"
                      value={buyerEmail}
                      disabled={busy}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                    />
                  </div>

                  {envelopeStyle !== "none" && (
                    <p className="para">
                      Sales tax on the envelope is added when the payment
                      starts, so the reader asks for a little more than{" "}
                      {centsToDollars(total)}. The gift card itself is not
                      taxed.
                    </p>
                  )}

                  {blocker && stage === "idle" && (
                    <p className="para">{blocker}</p>
                  )}
                  {message && (
                    <p className="para" role="status">
                      {message}
                    </p>
                  )}

                  {canCancelGiftSale(stage) && (
                    <button
                      type="button"
                      className="text-black reader-cancel"
                      onClick={cancelPayment}
                    >
                      Cancel payment
                    </button>
                  )}

                  <button
                    type="button"
                    className="section-main-btn"
                    disabled={Boolean(blocker) || busy}
                    onClick={takePayment}
                  >
                    {payLabel}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default GiftCard;
